// src/components/common/DateTimePicker.js
// PHASE FU-DUR2 VERIFIER: r1-fu-dur2-3e7a1c84
//
// Web custom DateTimePicker — replacement for <input type="datetime-local">
// which on macOS Chromium/Firefox ignores the `step` attribute for the minute
// picker. This component enforces 15-minute increments at the UI level.
//
// Renders as a button (display) → click opens a popover anchored beneath it.
// Combined view: calendar grid + time chips visible together. Popover closes
// on click-outside (window listener) or after selection (auto-close on time
// pick). UX target: Google Calendar's quick-event editor.
//
// Props:
//   value:    Date | string | null  — current value
//   onChange: (Date) => void        — fired when user picks a new datetime
//   disabled: boolean               — read-only state, button non-interactive
//   className: string               — passed through to the button element
//
// Notes on UTC vs local:
//   This picker operates in the user's local timezone, like datetime-local.
//   The Date object passed to onChange uses local time components.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW    = ['S','M','T','W','T','F','S'];

const HOURS_12  = [1,2,3,4,5,6,7,8,9,10,11,12];
const MINUTES   = [0, 15, 30, 45];

const formatDisplay = (d) => {
  if (!d) return 'Pick date & time';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return 'Pick date & time';
  const m = MONTHS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hour = date.getHours();
  const min = date.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${m} ${day}, ${year} · ${hour}:${String(min).padStart(2,'0')} ${ampm}`;
};

const DateTimePicker = ({ value, onChange, disabled, className = '' }) => {
  const [isOpen, setIsOpen]     = useState(false);
  const [popoverPos, setPopoverPos] = useState('below'); // 'below' | 'above'

  const safeValue = useMemo(() => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }, [value]);

  // Calendar navigation state — independent of the actual `value`
  const initialAnchor = safeValue || new Date();
  const [viewYear,  setViewYear]  = useState(initialAnchor.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialAnchor.getMonth());

  // When the popover opens, sync the calendar view to the current value
  useEffect(() => {
    if (isOpen) {
      const anchor = safeValue || new Date();
      setViewYear(anchor.getFullYear());
      setViewMonth(anchor.getMonth());
    }
  }, [isOpen, safeValue]);

  // Refs for click-outside + positioning
  const containerRef = useRef(null);
  const buttonRef    = useRef(null);
  const popoverRef   = useRef(null);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Smart positioning — flip above if there's no room below
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const popoverHeight = 420; // approximate
    const spaceBelow = window.innerHeight - rect.bottom;
    setPopoverPos(spaceBelow < popoverHeight && rect.top > popoverHeight ? 'above' : 'below');
  }, [isOpen]);

  // Derived calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth     = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  // Selected-state helpers
  const today = new Date(); today.setHours(0,0,0,0);
  const isSelectedDay = (d) =>
    safeValue && d === safeValue.getDate() && viewMonth === safeValue.getMonth() && viewYear === safeValue.getFullYear();
  const isToday = (d) =>
    d === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  // Currently chosen hour/minute (for time-chip highlighting)
  const curHour24 = safeValue ? safeValue.getHours() : 12;
  const curMin    = safeValue ? safeValue.getMinutes() : 0;
  const curHour12 = curHour24 % 12 || 12;
  const curIsPM   = curHour24 >= 12;

  // ── Helpers to commit a new date / time slice ────────────────────────────
  const commitDate = (day) => {
    const base = safeValue ? new Date(safeValue) : (() => { const n = new Date(); n.setHours(9,0,0,0); return n; })();
    const next = new Date(viewYear, viewMonth, day, base.getHours(), base.getMinutes(), 0, 0);
    onChange(next);
  };
  const commitHour12 = (h12, asPM) => {
    const base = safeValue ? new Date(safeValue) : new Date();
    let h24 = h12 % 12;
    if (asPM) h24 += 12;
    const next = new Date(base);
    next.setHours(h24, base.getMinutes() || 0, 0, 0);
    onChange(next);
  };
  const commitMinute = (m) => {
    const base = safeValue ? new Date(safeValue) : new Date();
    const next = new Date(base);
    next.setMinutes(m, 0, 0);
    onChange(next);
  };
  const commitAMPM = (asPM) => {
    commitHour12(curHour12, asPM);
  };
  const setToday = () => {
    const now = new Date();
    // Round to nearest 15 minutes
    const rounded = new Date(now);
    rounded.setMinutes(Math.round(now.getMinutes() / 15) * 15, 0, 0);
    onChange(rounded);
    setViewYear(rounded.getFullYear());
    setViewMonth(rounded.getMonth());
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        className={`mt-1 w-full p-2 border rounded-md text-left flex items-center justify-between text-sm bg-white hover:border-blue-400 transition-colors disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${isOpen ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300'} ${className}`}
      >
        <span className={safeValue ? 'text-gray-900' : 'text-gray-400'}>
          {formatDisplay(safeValue)}
        </span>
        <CalendarIcon size={16} className="text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className={`absolute z-50 left-0 ${popoverPos === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-[420px] max-w-[calc(100vw-2rem)]`}
        >
          {/* Calendar header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => {
                if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
                else setViewMonth(m => m - 1);
              }}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Previous month"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="font-semibold text-gray-900">{MONTHS[viewMonth]} {viewYear}</div>
            <button
              type="button"
              onClick={() => {
                if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
                else setViewMonth(m => m + 1);
              }}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Next month"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day-of-week labels */}
          <div className="grid grid-cols-7 mb-1">
            {DOW.map((d, i) => (
              <div key={i} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 mb-3">
            {cells.map((d, i) => {
              if (d === null) return <div key={`empty-${i}`} />;
              const sel = isSelectedDay(d);
              const td  = isToday(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => commitDate(d)}
                  className={`text-sm rounded-md py-1.5 transition-colors
                    ${sel ? 'bg-blue-600 text-white font-semibold' : 'hover:bg-gray-100 text-gray-700'}
                    ${!sel && td ? 'border border-blue-400 font-semibold' : ''}
                  `}
                >
                  {d}
                </button>
              );
            })}
          </div>

          {/* Time section */}
          <div className="border-t border-gray-200 pt-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Hour column */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Hour</div>
                <div className="grid grid-cols-3 gap-1">
                  {HOURS_12.map(h => {
                    const sel = curHour12 === h;
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => commitHour12(h, curIsPM)}
                        className={`text-sm py-1 rounded transition-colors ${sel ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}
                      >
                        {h}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-1 mt-1.5">
                  <button
                    type="button"
                    onClick={() => commitAMPM(false)}
                    className={`flex-1 text-xs py-1 rounded transition-colors ${!curIsPM ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => commitAMPM(true)}
                    className={`flex-1 text-xs py-1 rounded transition-colors ${curIsPM ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}
                  >
                    PM
                  </button>
                </div>
              </div>

              {/* Minute column */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Minute</div>
                <div className="grid grid-cols-2 gap-1">
                  {MINUTES.map(m => {
                    const sel = curMin === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => commitMinute(m)}
                        className={`text-sm py-1 rounded transition-colors ${sel ? 'bg-blue-600 text-white font-semibold' : 'bg-gray-50 hover:bg-gray-100 text-gray-700'}`}
                      >
                        :{String(m).padStart(2,'0')}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5">
                  <button
                    type="button"
                    onClick={setToday}
                    className="w-full text-xs py-1 rounded bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium"
                  >
                    Now
                  </button>
                </div>
              </div>
            </div>

            {/* Footer — close button */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {safeValue ? formatDisplay(safeValue) : ''}
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1 rounded hover:bg-blue-50"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;